import { Link, usePage, router } from '@inertiajs/react'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { formatLempiras } from '../../lib/format'
import { useCart, CartProvider, Cart } from '../../lib/cart'
import CartHeader from '../../lib/CartHeader'
import { PlusIcon } from '../../lib/icons'

interface Account { id: number; name: string; slug: string; logo_url?: string | null }
interface Variant {
  id: number; name: string; full_name: string; price_cents: number; price_with_tax_cents: number
  stock_quantity: number; sku: string
  images?: ProductImage[]    // variant-specific images (empty = use product images)
}
interface ProductImage { id: string; url: string; thumb_url: string; filename: string }
interface Product {
  id: number
  name: string
  description?: string
  price_cents: number
  price_with_tax_cents: number
  category_name?: string
  image_url?: string
  images?: ProductImage[]
  has_variants: boolean
  variants: Variant[]
  track_inventory?: boolean
  stock_quantity?: number | null
}
interface Props { account: Account; product: Product; cart?: Cart }

export default function CatalogShow(props: Props) {
  const { account, cart } = props
  return (
    <CartProvider cart={cart} slug={account.slug}>
      <CatalogShowInner {...props} />
    </CartProvider>
  )
}

function CatalogShowInner({ account, product }: { account: Account; product: Product }) {
  const [variantId, setVariantId] = useState<number | null>(
    product.has_variants ? product.variants[0]?.id ?? null : null
  )
  const [qty, setQty] = useState(1)
  const [justAdded, setJustAdded] = useState(false)
  const { addItem, items } = useCart()

  const selectedVariant = product.variants.find(v => v.id === variantId)
  const displayPrice = selectedVariant?.price_with_tax_cents ?? product.price_with_tax_cents

  const tracksStock = product.track_inventory === true
  const rawStock = product.has_variants
    ? (selectedVariant?.stock_quantity ?? 0)
    : (product.stock_quantity ?? 0)
  // null en backend = ilimitado. En ántier se serializa como number | null.
  const maxStock = tracksStock ? Number(rawStock ?? 0) : null
  const alreadyInCart = items
    .filter(i => i.product_id === product.id && (i.variant_id ?? null) === (variantId ?? null))
    .reduce((s, i) => s + i.quantity, 0)
  const remaining = maxStock === null ? null : Math.max(0, maxStock - alreadyInCart)
  const atMax = remaining !== null && remaining <= 0
  const canIncrease = remaining === null || qty < remaining

  const handleAdd = () => {
    if (atMax) return
    const toAdd = remaining === null ? qty : Math.min(qty, remaining)
    if (toAdd <= 0) return
    addItem(product.id, { variantId: variantId, quantity: toAdd })
    setJustAdded(true)
    setTimeout(() => setJustAdded(false), 2000)
  }

  // Si cambia el stock/variante y qty se pasa, bajarla
  useEffect(() => {
    if (remaining !== null && remaining > 0 && qty > remaining) {
      setQty(remaining)
    } else if (remaining === 0 && qty !== 1) {
      setQty(1)
    }
  }, [remaining, qty])

  // ── Image gallery state ──
  const productImages = product.images || []
  const variantImages = selectedVariant?.images || []
  // If variant has its own images, show those; otherwise show product images
  const images = variantImages.length > 0 ? variantImages : productImages
  const [activeImage, setActiveImage] = useState(0)

  // Reset active image to 0 when the image set changes (variant switch)
  const imageSetKey = useMemo(() => {
    if (variantImages.length > 0) return `variant-${selectedVariant?.id}`
    return `product`
  }, [variantImages.length, selectedVariant?.id])

  const handleVariantChange = useCallback((newVariantId: number) => {
    setVariantId(newVariantId)
    setActiveImage(0)
  }, [])

  const prevImage = () => setActiveImage(i => (i - 1 + images.length) % images.length)
  const nextImage = () => setActiveImage(i => (i + 1) % images.length)

  return (
    <div className="min-h-screen bg-gray-50">
      <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link href={`/${account.slug}/catalog`} className="text-[#55ACEE] hover:underline text-sm mb-4 inline-block">
          ← Volver al catálogo
        </Link>

        <div className="bg-white rounded-lg shadow overflow-hidden md:flex">
          {/* ── Image Gallery ── */}
          <div className="md:w-1/2">
            {images.length > 0 ? (
              <div className="relative">
                {/* Main image */}
                <div className="w-full aspect-square bg-gray-100 overflow-hidden relative">
                  <img
                    key={`${imageSetKey}-${activeImage}`}
                    src={images[activeImage].url}
                    alt={`${product.name} - imagen ${activeImage + 1}`}
                    className="w-full h-full object-contain"
                  />
                  {/* Prev/Next arrows (only if multiple images) */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-[#202020] transition-colors"
                        aria-label="Imagen anterior"
                      >‹</button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-[#202020] transition-colors"
                        aria-label="Imagen siguiente"
                      >›</button>
                    </>
                  )}
                  {/* Dot indicators (always shown if 2+ images) */}
                  {images.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveImage(i)}
                          className={`w-2 h-2 rounded-full transition-colors ${i === activeImage ? 'bg-[#55ACEE]' : 'bg-white/60'}`}
                          aria-label={`Ir a imagen ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full aspect-square bg-gray-200 flex items-center justify-center text-gray-400 text-6xl">📦</div>
            )}
          </div>

          {/* ── Product details ── */}
          <div className="md:w-1/2 p-6">
            {product.category_name && (
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{product.category_name}</p>
            )}
            <h1 className="text-2xl font-bold text-[#202020] mb-3">{product.name}</h1>
            <p className="text-3xl font-bold text-[#55ACEE] mb-1">{formatLempiras(displayPrice)}</p>
            <span className="text-xs text-gray-400">ISV incluido</span>

            {product.description && (
              <p className="text-gray-700 mb-4 whitespace-pre-line">{product.description}</p>
            )}

            {product.has_variants && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Variante</label>
                <select
                  value={variantId ?? ''}
                  onChange={(e) => handleVariantChange(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  {product.variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({formatLempiras(v.price_with_tax_cents)})
                    </option>
                  ))}
                </select>
                {selectedVariant && (
                  <p className="text-xs text-gray-500 mt-1">
                    {tracksStock && `Stock: ${selectedVariant.stock_quantity ?? 0} · `}SKU: {selectedVariant.sku}
                  </p>
                )}
              </div>
            )}

            {/* Stock visible para productos sin variantes */}
            {!product.has_variants && tracksStock && (
              <p className="text-sm text-gray-600 mb-3">
                Stock disponible: <span className="font-semibold text-[#202020]">{maxStock ?? 0}</span>
                {alreadyInCart > 0 && (
                  <span className="text-gray-400"> · {alreadyInCart} en carrito</span>
                )}
              </p>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-10 h-10 rounded bg-gray-200 hover:bg-gray-300"
                  aria-label="Disminuir cantidad"
                >−</button>
                <input
                  type="number" min="1"
                  max={remaining ?? undefined}
                  value={qty}
                  onChange={(e) => {
                    const n = Math.max(1, parseInt(e.target.value) || 1)
                    setQty(remaining === null ? n : Math.min(n, Math.max(1, remaining)))
                  }}
                  className="w-20 text-center px-3 py-2 border border-gray-300 rounded"
                  disabled={atMax}
                />
                {/* Solo mostrar + si aún se puede pedir más */}
                {canIncrease && (
                  <button
                    onClick={() => setQty(remaining === null ? qty + 1 : Math.min(qty + 1, remaining))}
                    className="w-10 h-10 rounded bg-gray-200 hover:bg-gray-300"
                    aria-label="Aumentar cantidad"
                  >+</button>
                )}
              </div>
              {tracksStock && remaining !== null && !atMax && (
                <p className="text-xs text-gray-500 mt-1">Puedes agregar hasta {remaining} más</p>
              )}
            </div>

            <button
              onClick={handleAdd}
              disabled={atMax}
              className={`w-full py-3 rounded font-bold flex items-center justify-center gap-2 transition-transform ${
                atMax
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#FFC107] text-[#202020] hover:bg-[#e6ad06] active:scale-95'
              }`}
            >
              <PlusIcon size={20} />
              {atMax ? 'Sin stock disponible' : justAdded ? '¡Agregado!' : 'Agregar al carrito'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
